import { useEffect, useRef, useState } from "react";
import { axisToDial, mergeMap, roleFor } from "../lib/console";

// Reads both USB button boxes through the Gamepad API and turns raw presses into named control events:
//   { type: "press", id, name } | { type: "toggle", id, name, on } | { type: "dial", id, name, value }
// Also accepts simulated events: window.dispatchEvent(new CustomEvent("spore-console", { detail })).
export const useConsole = ({ map: overrides, devices, enabled = true, onEvent, onRaw }) => {
  const [connected, setConnected] = useState({});
  const [dials, setDials] = useState({});
  const [toggles, setToggles] = useState({});
  const cb = useRef({ onEvent, onRaw });
  cb.current = { onEvent, onRaw };
  const cfg = useRef({ map: mergeMap(overrides), devices });
  cfg.current = { map: mergeMap(overrides), devices };
  const dialRef = useRef({});
  const toggleRef = useRef({});

  useEffect(() => {
    if (!enabled) return undefined;
    const prev = {};
    let frame;
    let lastConnected = "";

    const emit = (ev) => {
      if (ev.type === "dial") { dialRef.current[ev.id] = ev.value; setDials((d) => ({ ...d, [ev.id]: ev.value })); }
      if (ev.type === "toggle") { toggleRef.current[ev.id] = ev.on; setToggles((t) => ({ ...t, [ev.id]: ev.on })); }
      cb.current.onEvent?.(ev);
    };

    const button = (role, index, pressed, first) => {
      const { map } = cfg.current;
      for (const [id, c] of Object.entries(map)) {
        if (c.device !== role) continue;
        if (c.kind === "button" && c.index === index) {
          if (c.type === "toggle") { if (first) { toggleRef.current[id] = pressed; setToggles((t) => ({ ...t, [id]: pressed })); } else emit({ type: "toggle", id, name: c.name, on: pressed }); }
          else if (pressed && !first) emit({ type: "press", id, name: c.name });
        } else if (c.kind === "encoder" && pressed && !first && (c.cw === index || c.ccw === index)) {
          const cur = dialRef.current[id] ?? 5;
          emit({ type: "dial", id, name: c.name, value: Math.max(1, Math.min(10, cur + (c.cw === index ? 1 : -1))) });
        }
      }
    };

    const axis = (role, index, value, first) => {
      const { map } = cfg.current;
      for (const [id, c] of Object.entries(map)) {
        if (c.device !== role || c.kind !== "axis" || c.index !== index) continue;
        const v = axisToDial(value);
        if (first) { dialRef.current[id] = v; setDials((d) => ({ ...d, [id]: v })); } else if (dialRef.current[id] !== v) emit({ type: "dial", id, name: c.name, value: v });
      }
    };

    const poll = () => {
      const pads = Array.from(navigator.getGamepads?.() || []).filter(Boolean);
      const roles = {};
      const taken = [];
      for (const p of pads) { roles[p.index] = roleFor(p.id, cfg.current.devices, taken); taken.push(roles[p.index]); }
      const summary = pads.map((p) => `${p.index}:${roles[p.index]}:${p.id}:${p.buttons.length}:${p.axes.length}`).join("|");
      if (summary !== lastConnected) {
        lastConnected = summary;
        const next = {};
        pads.forEach((p) => { next[roles[p.index]] = { id: p.id, index: p.index, buttons: p.buttons.length, axes: p.axes.length }; });
        setConnected(next);
      }
      for (const p of pads) {
        const role = roles[p.index];
        const first = !prev[p.index];
        const was = prev[p.index] || { buttons: [], axes: [] };
        p.buttons.forEach((b, i) => {
          const pressed = b.pressed || b.value > 0.5;
          if (first || pressed !== Boolean(was.buttons[i])) {
            if (!first && pressed) cb.current.onRaw?.({ device: role, kind: "button", index: i, gamepadId: p.id });
            button(role, i, pressed, first);
          }
        });
        p.axes.forEach((a, i) => {
          if (first || Math.abs(a - (was.axes[i] ?? 0)) > 0.02) {
            if (!first && Math.abs(a - (was.axes[i] ?? 0)) > 0.3) cb.current.onRaw?.({ device: role, kind: "axis", index: i, value: a, gamepadId: p.id });
            axis(role, i, a, first);
          }
        });
        prev[p.index] = { buttons: p.buttons.map((b) => b.pressed || b.value > 0.5), axes: [...p.axes] };
      }
      for (const k of Object.keys(prev)) if (!pads.find((p) => String(p.index) === k)) delete prev[k];
      frame = requestAnimationFrame(poll);
    };

    const onSim = (e) => {
      const d = e.detail || {};
      const c = cfg.current.map[d.id];
      if (!c) return;
      if (d.type === "dial") emit({ type: "dial", id: d.id, name: c.name, value: Math.max(1, Math.min(10, Number(d.value) || 5)) });
      else if (c.type === "toggle") { const on = d.on ?? !toggleRef.current[d.id]; toggleRef.current[d.id] = on; emit({ type: "toggle", id: d.id, name: c.name, on }); }
      else emit({ type: "press", id: d.id, name: c.name });
    };

    frame = requestAnimationFrame(poll);
    window.addEventListener("spore-console", onSim);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("spore-console", onSim); };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { connected, dials, toggles, map: cfg.current.map };
};
